import React from 'react';

/**
 * OpenTvLoader — logo padrão de carregamento do OPEN TV (arte: Logo_p.svg).
 *
 * Usado em TODO carregamento: abertura do app, sintonizar canal/filme/série, carregamentos
 * dentro das listas e telas que estão carregando.
 *  - O círculo de processamento SEMPRE gira dentro da tela da TV.
 *  - Com `image` (capa do canal/filme/série): a capa aparece dentro da tela, com opacidade,
 *    e o círculo gira por cima dela.
 *  - Sem `image`: só o círculo.
 *
 * COMPATIBILIDADE (importante — Stick é Chrome 83 / build mira Chrome 64):
 *  - NÃO usar `aspect-ratio` (só existe a partir do Chrome 88). A altura proporcional da caixa do
 *    logo é feita com `height: 0 + padding-bottom: 120.08%` (proporção 19120 x 22960 da arte),
 *    técnica que funciona em qualquer navegador. A versão anterior usava `aspectRatio` e, no
 *    stick, a caixa ficava com altura zero: TV, capa e círculo sumiam, só o texto aparecia.
 *  - Posições internas com top/left/width/height explícitos (sem `inset` em estilo inline).
 *
 * Desempenho: TV e texto são fixos; só o círculo gira, como elemento separado (rotação na GPU).
 * Círculo: SÓ o arco em movimento (sem trilho/contorno de fundo), igual à animação de boot.
 *
 * size:
 *  - 'hero'    → grande, proporcional à altura da tela (≈42% da altura, igual à animação de boot).
 *               Para telas inteiras no Stick: abertura do app, sintonizando, carregando.
 *  - 'full'    → tamanho médio fixo (Web: tela "Sintonizando" dentro do player).
 *  - 'compact' → pequeno, dentro das listas ("Carregando mais canais...").
 *
 * glow:
 *  - 'full'   → brilho do Web: aura dourada desfocada pulsando + sombra de 30px na moldura.
 *  - 'static' → só a sombra fixa (igual ao brilho da animação de boot). Recomendado no Stick.
 *  - 'none'   → sem sombra (TVs mais fracas/antigas).
 */

export type OpenTvLoaderGlow = 'full' | 'static' | 'none';

export interface OpenTvLoaderProps {
  image?: string;
  status?: string;
  subtitle?: string;
  title?: string;
  glow?: OpenTvLoaderGlow;
  size?: 'hero' | 'full' | 'compact';
  className?: string;
}

const VIEWBOX = '-2160 -980 19120 22960';
// Proporção da arte (altura / largura) = 22960 / 19120
const HEIGHT_RATIO = '120.08%';
// Geometria medida sobre a arte, em % da caixa do logo
const FILL: React.CSSProperties = { position: 'absolute', top: 0, left: 0, width: '100%', height: '100%' };
const COVER_BOX: React.CSSProperties = { position: 'absolute', left: '15.75%', top: '30.06%', width: '68.45%', height: '37.80%' };
const SPINNER_BOX: React.CSSProperties = { position: 'absolute', left: '31.80%', top: '33.82%', width: '36.35%', height: '30.27%' };
const AURA_BOX: React.CSSProperties = { position: 'absolute', left: '8%', top: '18%', width: '84%', height: '62%' };

const FRAME_D = 'M10893.74 -634.54c305.49,-305.76 801.17,-305.76 1106.93,0 305.76,305.49 305.76,801.17 0,1106.93l-2754.44 2754.44 4801.76 0c766.5,0 1464.96,314.16 1972.71,820.61l2.89 2.89c506.44,507.76 820.87,1206.22 820.87,1972.71l0 8484.77c0,766.23 -314.43,1464.69 -820.87,1972.45l-2.89 2.89c-507.76,506.44 -1206.22,820.87 -1972.71,820.87l-13296.25 0c-766.5,0 -1464.69,-314.43 -1972.45,-820.87l-2.89 -2.89c-506.44,-507.76 -820.87,-1206.22 -820.87,-1972.45l0 -8484.77c0,-766.5 314.43,-1464.96 820.87,-1972.71l2.89 -2.89c507.76,-506.44 1205.96,-820.61 1972.45,-820.61l4801.76 0 -2754.18 -2754.44c-305.76,-305.76 -305.76,-801.43 0,-1106.93 305.76,-305.76 801.17,-305.76 1106.93,0l3493.62 3493.62 3493.88 -3493.62 -0.01 0zm-10142.01 5339.99c-363.28,0 -692.42,147.36 -929.88,384.82l-2.89 2.89c-237.2,237.2 -384.56,566.33 -384.56,929.88l0 8484.77c0,363.28 147.36,692.42 384.56,929.62l2.89 2.89c237.46,237.46 566.6,384.82 929.88,384.82l13296.25 0c363.55,0 692.68,-147.36 929.88,-384.82l2.89 -2.89c237.46,-237.2 384.82,-566.33 384.82,-929.62l0 -8484.77c0,-363.55 -147.36,-692.68 -384.82,-929.88l-2.89 -2.89c-237.2,-237.2 -566.33,-384.82 -929.88,-384.82 -4437.42,0 -8858.82,0 -13296.25,0zm0 565.55l13296.25 0c413.72,0 752.05,338.33 752.05,751.79l0 8484.77c0,413.72 -338.33,752.05 -752.05,752.05l-13296.25 0c-413.72,0 -752.05,-338.33 -752.05,-752.05l0 -8484.77c0,-413.46 338.33,-751.79 752.05,-751.79zm0 156.29c-327.3,0 -595.75,268.19 -595.75,595.49l0 8484.77c0,327.3 268.46,595.75 595.75,595.75l13296.25 0c327.56,0 595.75,-268.46 595.75,-595.75l0 -8484.77c0,-327.3 -268.19,-595.49 -595.75,-595.49l-13296.25 0z';
const TEXT_D = 'M2239.54 19625.31l284.22 0c210.41,0 382.46,172.05 382.46,382.2l0 0c0,210.41 -172.05,382.46 -382.46,382.46l-284.22 0 0 -764.66zm9141.47 -586.56l0 693.21 785.67 0 0 2131.9 804.85 0 0 -2131.9 785.67 0 0 -693.21 -785.67 0 -804.85 0 -785.67 0zm-2764.95 2825.1l62.78 0 742.07 0 62.78 0 0 -2825.1 -804.85 0 0 1718.18 -1152.37 -1718.18 -804.85 0 0 2825.1 804.85 0 0 -1624.66 1089.59 1624.66zm-3551.41 -1065.94l897.31 0 0 -692.95 -897.31 0 0 -373 897.31 0 0 -693.21 -897.31 0 -455.22 0 -349.89 0 0 2825.1 349.89 0 455.22 0 897.31 0 0 -692.95 -897.31 0 0 -373 0 0.01zm9959.71 1065.94l105.86 0 698.99 0 105.86 0 909.39 -2825.1 -805.11 0 -559.5 1739.46 -559.77 -1739.46 -804.85 0 909.13 2825.1zm-13589.66 0l804.85 0 0 -887.59 502.77 0c532.71,0 968.76,-435.78 968.76,-968.76l0 0c0,-532.71 -436.05,-968.76 -968.76,-968.76l-502.77 0 -804.85 0 0 1937.51 0 887.59 0 0.01zm-2103 -2825.1c760.19,0 1376.17,632.53 1376.17,1412.68 0,780.15 -615.98,1412.42 -1376.17,1412.42 -759.93,0 -1376.17,-632.27 -1376.17,-1412.42 0,-780.15 616.24,-1412.68 1376.17,-1412.68zm0 693.21c315.48,0 571.33,322.04 571.33,719.48 0,397.43 -255.85,719.48 -571.33,719.48 -315.48,0 -571.33,-322.04 -571.33,-719.48 0,-397.43 255.85,-719.48 571.33,-719.48z';

export const OpenTvLoader: React.FC<OpenTvLoaderProps> = ({
  image,
  status,
  subtitle,
  title,
  glow = 'static',
  size = 'full',
  className = '',
}) => {
  const isCompact = size === 'compact';
  const widthClass = size === 'compact' ? 'w-14' : size === 'full' ? 'w-36 sm:w-44' : '';
  // 'hero': V8.5.2 — MESMO tamanho do logo de entrada do index.html (35vh, no máximo 80% da largura), para a troca
  // entre o logo de entrada e o de carregamento (Gerenciador, perfil, canais) não "pular" de tamanho.
  // V8.8.7: no máximo metade da largura (antes 80%) — no celular em pé o logo ficava enorme.
  const widthStyle: React.CSSProperties | undefined =
    size === 'hero' ? { width: '35vh', maxWidth: '50vw' } : undefined;
  // V8.5.1: Web e Gerenciador sem sombra/brilho amarelo atrás do logo (igual ao Stick). O parâmetro glow é aceito
  // mas ignorado, para não precisar mexer em quem chama.
  void glow;
  const showShadow = false;

  return (
    <div className={`flex flex-col items-center justify-center text-center select-none ${className}`}>
      <div className={`relative ${widthClass}`} style={widthStyle}>
        {/* Caixa com altura proporcional sem aspect-ratio (compatível com Chrome 64+) */}
        <div style={{ position: 'relative', width: '100%', height: 0, paddingBottom: HEIGHT_RATIO }}>
          {false && (
            <div
              className="rounded-full blur-2xl animate-pulse"
              style={{ ...AURA_BOX, backgroundColor: 'rgba(255, 170, 0, 0.25)' }}
            />
          )}

          {image && (
            <div className="overflow-hidden bg-black" style={{ ...COVER_BOX, borderRadius: '6%' }}>
              <img
                src={image}
                alt={title || ''}
                style={{ width: '100%', height: '100%', objectFit: 'contain', opacity: 0.55 }}
                onError={(e) => {
                  (e.target as HTMLElement).style.display = 'none';
                }}
              />
            </div>
          )}

          <svg
            viewBox={VIEWBOX}
            style={{ ...FILL, ...(showShadow ? { filter: 'drop-shadow(0 0 30px rgba(255, 170, 0, 0.55))' } : {}) }}
            aria-hidden="true"
          >
            <path fill="#FFAA00" d={FRAME_D} />
            <path fill="#FEFEFE" fillRule="evenodd" d={TEXT_D} />
          </svg>

          <div className="animate-spin" style={{ ...SPINNER_BOX, animationDuration: '0.9s' }}>
            <svg viewBox="0 0 6950 6950" style={{ display: 'block', width: '100%', height: '100%' }} aria-hidden="true">
              <defs>
                <linearGradient id="otv-loader-arc" x1="0" y1="0" x2="1" y2="1">
                  <stop offset="0" stopColor="#FFAA00" stopOpacity="0" />
                  <stop offset="0.55" stopColor="#FFAA00" stopOpacity="0.5" />
                  <stop offset="1" stopColor="#FFC233" stopOpacity="1" />
                </linearGradient>
              </defs>
              <circle
                cx="3475"
                cy="3475"
                r="3055"
                fill="none"
                stroke="url(#otv-loader-arc)"
                strokeWidth="840"
                strokeLinecap="round"
                strokeDasharray="11901 19195"
                transform="rotate(-90 3475 3475)"
              />
            </svg>
          </div>
        </div>
      </div>

      {status && (
        <div
          className={`font-extrabold uppercase tracking-widest ${
            isCompact ? 'text-[10px] mt-1.5' : 'text-xs sm:text-sm mt-3 sm:mt-4'
          }`}
          style={{ color: '#FFAA00' }}
        >
          {status}
        </div>
      )}
      {subtitle && !isCompact && (
        <p className="mt-1 text-amber-300/85 text-[11px] sm:text-xs max-w-[280px] sm:max-w-md px-3 leading-snug font-medium">
          {subtitle}
        </p>
      )}
      {title && !isCompact && (
        <h3 className="mt-1.5 sm:mt-2 text-zinc-200 font-bold text-xs sm:text-sm md:text-base px-4 leading-snug max-w-[290px] sm:max-w-md truncate">
          {title}
        </h3>
      )}
    </div>
  );
};
